import { cx } from "@suse-o11y/class-variance";
import { IconErrorOutlined } from "@suse-o11y/icons";
import type { Record } from "@suse-o11y/record";
import { useMonitor } from "@suse-o11y/signal";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";

import { TableCell } from "~/components/table/cell";
import { TableRow } from "~/components/table/row";
import { Truncated } from "~/components/truncated";

import type { DashboardVariableKind } from "../../common/dashboard-spec";

import type { VariableFormState } from "./_form-state";

const KIND_TO_TYPE = {
	TextVariable: "Text",
	ListVariable: "List",
} satisfies Record<DashboardVariableKind>;

export interface VariableRowProps extends React.ComponentPropsWithoutRef<typeof TableRow> {
	variable: VariableFormState;
	handle: React.ReactNode;
	actions: React.ReactNode;
}

export const VariableRow = React.forwardRef<HTMLDivElement, VariableRowProps>(
	({ className, variable, handle, actions, ...props }, ref) => {
		const monitor = useMonitor();

		const input = variable.getInput(monitor);
		const error = variable.getError(monitor);

		return (
			<TableRow
				ref={ref}
				{...props}
				className={cx("ring-grey-200 data-dragging:border-none data-dragging:ring-1", className)}
			>
				<TableCell className="empty:p-0">{handle}</TableCell>

				<TableCell className="gap-2">
					<Truncated className="code" el="code">
						{input.name}
					</Truncated>

					{error && (
						<TooltipBoundary disableHoverableContent>
							<TooltipTrigger asChild>
								<span className="cursor-help text-red-500">
									<IconErrorOutlined height={20} width={16} />
								</span>
							</TooltipTrigger>

							<Tooltip className="tooltip/tooltip">{error}</Tooltip>
						</TooltipBoundary>
					)}
				</TableCell>

				<TableCell className="gap-2">
					<Truncated>{input.label ?? input.name}</Truncated>

					{error && (
						<TooltipBoundary disableHoverableContent>
							<TooltipTrigger asChild>
								<span className="cursor-help text-red-500">
									<IconErrorOutlined height={20} width={16} />
								</span>
							</TooltipTrigger>

							<Tooltip className="tooltip/tooltip">{error}</Tooltip>
						</TooltipBoundary>
					)}
				</TableCell>

				<TableCell>{KIND_TO_TYPE[input.spec.kind]}</TableCell>

				<TableCell>{input.description || "-"}</TableCell>

				<TableCell>{actions}</TableCell>
			</TableRow>
		);
	},
);
